import {
  leadTimeCompositionSnapshotSchema,
  resolvedPlanningLevelsSchema,
} from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { StockSurplusSchema } from "./stock-surplus";
import { ForecastLeadTimeSourceSchema } from "./forecast-runs.contract";

/**
 * One SKU's forecast, read wide across a scope of locations.
 *
 * The demand grid answers "which series need attention"; this answers "why does
 * this one look like that". The question is asked of an ITEM, not of an item at
 * one place: a buyer holding 1/2" EMT wants the item's shape across the North
 * division, and narrowing to a single branch is one more turn of the same
 * filter rather than a different page. So the location is a filter here, and
 * everything below is the aggregate over whatever it leaves standing.
 *
 * Aggregation is not uniform, and the shapes say which rule applied:
 *
 * - Quantities SUM. Demand, stock, and the policy levels are all counts of
 *   units at places, and the scope's total is a real number.
 * - Rates and settings do not sum. Lead time, service level and the z it
 *   implies come back as {@link ForecastDetailRangeSchema} — the low, the high
 *   and the mean across the scope — because a scope holding 3 and 21 days has
 *   no single lead time and averaging it away hides exactly the split a buyer
 *   is looking for.
 * - Labels come back as the distinct set. Two methods across twelve locations
 *   is a fact worth seeing, and "Croston" alone would be a lie about ten of
 *   them.
 */

// ─── Aggregation primitives ─────────────────────────────────────────────────

/**
 * A non-additive number across the scope. Null-free: a range exists only where
 * at least one location had a value, so `null` for the whole object means "no
 * location reported this", and `min === max` means the scope agrees.
 */
export const ForecastDetailRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
  /** Unweighted mean over the locations that had a value. */
  avg: z.number(),
  /** How many locations contributed — the denominator behind `avg`. */
  count: z.number(),
});
export type ForecastDetailRange = z.infer<typeof ForecastDetailRangeSchema>;

// ─── Scope ──────────────────────────────────────────────────────────────────

export const ForecastDetailLocationSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string().nullable(),
  /** Source-system location code (`im_warehouses.origin_id`). */
  warehouseCode: z.string().nullable(),
  warehouseType: z.string().nullable(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
});
export type ForecastDetailLocation = z.infer<
  typeof ForecastDetailLocationSchema
>;

/**
 * Which locations the numbers cover, resolved server-side from the filters.
 *
 * The list is returned in full rather than as a count, because every write on
 * this page targets a location and the client needs to name them. A scope
 * wider than {@link SCOPE_LOCATION_LIMIT} is refused rather than truncated: a
 * truncated list would silently make "apply to all" mean "apply to some".
 */
export const ForecastDetailScopeSchema = z.object({
  locations: z.array(ForecastDetailLocationSchema),
  /** Distinct divisions across those locations. */
  divisionCount: z.number(),
});
export type ForecastDetailScope = z.infer<typeof ForecastDetailScopeSchema>;

/** Locations one detail read will aggregate before it refuses the scope. */
export const SCOPE_LOCATION_LIMIT = 500;

// ─── Identity ───────────────────────────────────────────────────────────────

/**
 * The item, plus the attributes its locations carry. Those attributes are
 * per-location in the database (a SKU can be stocked in Dallas and special-order
 * in Austin), so each is the distinct set across the scope, sorted, and empty
 * where no location says anything.
 */
export const ForecastDetailIdentitySchema = z.object({
  sku: z.string(),
  skuLabel: z.string().nullable(),
  productName: z.string().nullable(),
  /** Current ProductV3 catalogue state, independent of the selected run. */
  productIsActive: z.boolean().nullable(),
  productIsSellable: z.boolean().nullable(),
  stockStatuses: z.array(z.string()),
  popularityCodes: z.array(z.string()),
  /**
   * AS400 buyer codes across the scope (`im_inventory_policies.buyer_code`,
   * falling back to the legacy metadata key). The field name predates the
   * distinction: these are buyer codes, not inventory planner codes.
   */
  plannerCodes: z.array(z.string()),
  warehouseTypes: z.array(z.string()),
  /** Distinct names of the locations these ones replenish from. */
  replenishesFrom: z.array(z.string()),
});
export type ForecastDetailIdentity = z.infer<
  typeof ForecastDetailIdentitySchema
>;

// ─── The time grid ──────────────────────────────────────────────────────────

/**
 * The month axis is two ranges laid end to end: the history the run consumed,
 * then the horizon it wrote. They are returned separately because no column is
 * ever both — a history month has no forecast and a horizon month has no
 * actual — and merging them would need a null in every row to say which half
 * you are in.
 *
 * Every vector is the scope's sum for that month.
 */
export const ForecastDetailDemandSchema = z.object({
  /** History months, ascending. Empty when the run recorded no input vector. */
  historyMonths: z.array(z.string()),
  /** Horizon months, ascending, after any month-range filter. */
  months: z.array(z.string()),
  /**
   * Demand as it happened, before any buyer edit. Aligned to `historyMonths`.
   *
   * Null when it cannot be reconstructed for this view — an older run, or a
   * location with no division to aggregate through. Explicitly absent rather
   * than a copy of `adjustedHistory`, because a caller comparing the two would
   * read equal vectors as "nothing was edited" on a scope that was. One
   * location that cannot be reconstructed nulls the whole vector: a partial sum
   * beside a complete one is a difference that is not an edit.
   */
  rawHistory: z.array(z.number()).nullable(),
  /**
   * The same months after persisted history overrides — this is what the run
   * actually consumed. Identical to `rawHistory` when nothing is overridden.
   */
  adjustedHistory: z.array(z.number()),
  /** The run's output, aligned to `months`. */
  forecast: z.array(z.number().nullable()),
  lower: z.array(z.number().nullable()),
  upper: z.array(z.number().nullable()),
  /**
   * The forecast ordering will use, for months where any location in scope has
   * a pin; null where none does. A pin replaces its location's number, so this
   * is each pinned location's pin plus every other location's engine forecast —
   * not the pins alone, which would drop the unpinned locations from the total.
   * At one location it is simply the pin. Shown in place of `forecast`, which
   * stays the engine's own figure for comparison.
   */
  pinnedForecast: z.array(z.number().nullable()),
  /**
   * The scope's effective growth/shrinkage factor for that month: the summed
   * forecast over the summed unfactored forecast. Null where no location
   * applied one. Unlike a pin it is folded into the number.
   */
  appliedFactor: z.array(z.number().nullable()),
  /**
   * Per month, how many locations in scope carry an active override for it —
   * the mark the grid draws on a settable cell. Aligned to `historyMonths` and
   * `months` respectively.
   */
  historyOverrideCounts: z.array(z.number()),
  forecastOverrideCounts: z.array(z.number()),
  /**
   * Per history month, how many locations in scope had that month capped or
   * excluded by an outlier rule in the run they read (GEN-2301). Aligned to
   * `historyMonths`. The unedited figure is in `rawHistory`.
   */
  historyOutlierCounts: z.array(z.number()).optional(),
});
export type ForecastDetailDemand = z.infer<typeof ForecastDetailDemandSchema>;

// ─── What the run did ───────────────────────────────────────────────────────

/**
 * The demand basis a run recorded for runs that did not record one: the
 * engine's original outbound history (sales plus transfers out). Saved runs are
 * never reinterpreted, so an unversioned run is this basis for good.
 */
export const LEGACY_DEMAND_BASIS = "legacy_outbound_v1";

export const ForecastDetailRunContextSchema = z.object({
  leadTimeCompositions: z.array(leadTimeCompositionSnapshotSchema).optional(),
  runId: z.string().uuid(),
  asOfDate: z.string().nullable(),
  /** How many of the scope's locations the run actually holds a series for. */
  seriesCount: z.number(),
  demandClasses: z.array(z.string()),
  methods: z.array(z.string()),
  leadTimeSources: z.array(ForecastLeadTimeSourceSchema),
  leadTimeDaysUsed: ForecastDetailRangeSchema.nullable(),
  leadTimeSigmaDays: ForecastDetailRangeSchema.nullable(),
  /** Receipts sampled, summed — this one IS a count. */
  leadTimeSampleN: z.number().nullable(),
  /** Service-level z the safety stock was sized on. */
  zUsed: ForecastDetailRangeSchema.nullable(),
  targetAvailabilityUsed: ForecastDetailRangeSchema.nullable(),
  /** Locations whose history the run consumed with an override in force. */
  overrideAppliedCount: z.number(),
  /**
   * Distinct demand bases of the runs behind the scope's series, read from each
   * run's `method_config.demandBasis` — {@link LEGACY_DEMAND_BASIS} where a run
   * recorded none. More than one means the totals mix bases.
   */
  demandBases: z.array(z.string()),
});
export type ForecastDetailRunContext = z.infer<
  typeof ForecastDetailRunContextSchema
>;

// ─── Policy and position ────────────────────────────────────────────────────

export const ForecastDetailPolicySchema = z.object({
  /** Current legacy resolution for one location only; absent on older payloads. */
  planningLevels: resolvedPlanningLevelsSchema.nullish(),
  minQuantity: z.number().nullable(),
  maxQuantity: z.number().nullable(),
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  minFloor: z.number().nullable(),
  effectiveTriggerLevel: z.number(),
  /** Distinct sources across the scope; one entry where the scope agrees. */
  effectiveLevelSources: z.array(
    z.enum([
      "forecast_safety_stock_override",
      "forecast_calculated",
      "min_override",
      "fixed_min",
      "min_calculated",
    ]),
  ),
  minFloorAppliedCount: z.number(),
  targetAvailability: ForecastDetailRangeSchema.nullable(),
  calculationMethods: z.array(z.string()),
  /** Oldest recalculation across the scope — the age of the stalest level. */
  lastCalculatedAt: z.string().nullable(),
  /** Locations with a buyer's policy override in force right now. */
  activeOverrideCount: z.number(),
  /** Locations that have a policy row at all. */
  policyCount: z.number(),
});
export type ForecastDetailPolicy = z.infer<typeof ForecastDetailPolicySchema>;

export const ForecastDetailPositionSchema = z.object({
  onHand: z.number().nullable(),
  reserved: z.number().nullable(),
  onOrder: z.number(),
  /** On hand less what is already committed, plus nothing on order. */
  available: z.number().nullable(),
  /** Oldest snapshot in the scope: how stale the stock picture could be. */
  snapshotDate: z.string().nullable(),
  /** Locations that have ever been counted. */
  countedLocations: z.number(),
  /**
   * The latest stock snapshot behind each counted location, oldest first —
   * where and when each part of `onHand` was read. A snapshot is the daily
   * stock-file row, not a physical cycle count. Locations with no snapshot are
   * absent rather than zero.
   */
  snapshots: z.array(
    z.object({
      warehouseId: z.string().uuid(),
      snapshotDate: z.string(),
      onHand: z.number().nullable(),
    }),
  ),
});
export type ForecastDetailPosition = z.infer<
  typeof ForecastDetailPositionSchema
>;

/**
 * The source system's own numbers for the same scope, carried so a buyer can
 * see the two agree before acting on ours. Null when no location in scope has a
 * row there, which for a SKU nobody stocks is the normal case.
 */
export const ForecastDetailSourcePositionSchema = z.object({
  onHand: z.number().nullable(),
  unshipped: z.number().nullable(),
  onOrder: z.number().nullable(),
  bookedQty: z.number().nullable(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  netAvailable: z.number().nullable(),
});
export type ForecastDetailSourcePosition = z.infer<
  typeof ForecastDetailSourcePositionSchema
>;

// ─── Derived ────────────────────────────────────────────────────────────────

/** Why customer sales are unavailable for a view, from the customer-sales reads. */
export const CustomerSalesUnavailableReasonSchema = z.enum([
  "no_transactions",
  "source_overlap_conflict",
  "missing_source_identity",
  /**
   * The source rows cannot be proven to be realized customer sales: an
   * unproven AS400 source class or canonical division, or no authoritative
   * shipped quantity and date. Never stood in for by ordered quantity or
   * order date.
   */
  "source_incompatible",
]);
export type CustomerSalesUnavailableReason = z.infer<
  typeof CustomerSalesUnavailableReasonSchema
>;

/**
 * Where the hits and demand size came from: direct customer sales at
 * `warehouseCount` locations, for the windows ending `endMonth`. Unavailable
 * means the source could not count them once — never a fallback to monthly
 * history, which would quietly change what a hit means.
 */
export const ForecastDetailCustomerSalesSchema = z.object({
  availability: z.enum(["available", "unavailable"]),
  availabilityReason: CustomerSalesUnavailableReasonSchema.nullable(),
  /** Inclusive last complete month of the windows, YYYY-MM-01. */
  endMonth: z.string(),
  /** Locations whose direct sales were counted. */
  warehouseCount: z.number(),
  /** Distinct customer orders with positive sales; null when unavailable. */
  hits6: z.number().nullable(),
  hits9: z.number().nullable(),
  hits12: z.number().nullable(),
  priorYearHits: z.number().nullable(),
  /** Net trailing-12-month customer quantity per positive sales order. */
  avgOrderSize: z.number().nullable(),
});
export type ForecastDetailCustomerSales = z.infer<
  typeof ForecastDetailCustomerSalesSchema
>;

/**
 * Legacy monthly statistics remain for API compatibility. New consumers must
 * use customerSales for transactional customer-order metrics.
 */
export const ForecastDetailDerivedSchema = z.object({
  /** @deprecated Mean positive monthly history quantity, not order size. */
  avgDemandSize: z.number().nullable().meta({
    deprecated: true,
    description:
      "Legacy mean of positive history months; use customerSales.avgOrderSize for customer order size.",
  }),
  /** @deprecated Positive-demand months in the trailing 6 months. */
  hits6: z.number().meta({
    deprecated: true,
    description:
      "Legacy positive-demand month count; use customerSales.hits6 for customer orders.",
  }),
  /** @deprecated Positive-demand months in the trailing 9 months. */
  hits9: z.number().meta({
    deprecated: true,
    description:
      "Legacy positive-demand month count; use customerSales.hits9 for customer orders.",
  }),
  /** @deprecated Positive-demand months in the trailing 12 months. */
  hits12: z.number().meta({
    deprecated: true,
    description:
      "Legacy positive-demand month count; use customerSales.hits12 for customer orders.",
  }),
  /** @deprecated Positive-demand months in the preceding 12 months. */
  priorYearHits: z.number().meta({
    deprecated: true,
    description:
      "Legacy prior-year positive-demand month count; use customerSales.priorYearHits for customer orders.",
  }),
  customerSales: ForecastDetailCustomerSalesSchema,
  /** Locations that replenish from one of the scope's locations. */
  dependentSpokeCount: z.number(),
  /** @deprecated Alias of stockSurplus.quantity; prefer its structured explanation. */
  surplusQty: z.number().nullable(),
});
export type ForecastDetailDerived = z.infer<typeof ForecastDetailDerivedSchema>;

// ─── Demand coverage ────────────────────────────────────────────────────────

/**
 * How the demand vectors were assembled from the scope's series, from each
 * series' RECORDED customer-demand plan — never today's network.
 *
 * - `legacy_outbound_v1`: saved runs with no plan. Every series adds, as it
 *   always has.
 * - `served_customer_sales_v1`: a hub's series already includes the spokes it
 *   served, so a selected spoke its recorded footprint covers is shown but not
 *   added again (`included_in_selected_hub`).
 * - `mixed_or_unknown`: the footprints cannot be proven not to overlap, so the
 *   demand vectors are withheld (`availability: "unavailable"`).
 */
export const ForecastDetailDemandCoverageSchema = z.object({
  availability: z.enum(["available", "unavailable"]),
  demandBasis: z.string(),
  represented: z.array(
    z.object({
      warehouseId: z.string().uuid(),
      forecastRunId: z.string().uuid(),
      coveredByWarehouseId: z.string().uuid().nullable(),
      reason: z.enum([
        "contributing",
        "included_in_selected_hub",
        "legacy_basis",
        "unproven_overlap",
      ]),
    }),
  ),
});
export type ForecastDetailDemandCoverage = z.infer<
  typeof ForecastDetailDemandCoverageSchema
>;

/**
 * Coverage per vector, because the evidence differs. History is covered by the
 * recorded run inputs; a forecast only by a hub that actually wrote output in
 * its run's stored horizon — a hub with inputs but no output cannot stand in
 * for a spoke's forecast. Each is null when the scope holds no series of that
 * kind.
 */
export const ForecastDetailDemandCoverageSetSchema = z.object({
  history: ForecastDetailDemandCoverageSchema.nullable(),
  forecast: ForecastDetailDemandCoverageSchema.nullable(),
  /**
   * Visible horizon months whose forecast total is unknown: a selected hub
   * covers a spoke's forecast but wrote nothing for that month. The forecast,
   * band, factor and pinned values are null there, never zero.
   */
  forecastUnknownMonths: z.array(z.string()),
});
export type ForecastDetailDemandCoverageSet = z.infer<
  typeof ForecastDetailDemandCoverageSetSchema
>;

// ─── Accuracy ───────────────────────────────────────────────────────────────

/**
 * The scored monthly ledger, summed over the scope; unscored months are absent
 * rather than zero.
 *
 * The quantity columns add, because each is a count of units at a place. The
 * scaled error does not — it is a ratio, and averaging one location's ratio
 * with another's weights a branch that sold three units the same as a hub that
 * sold three thousand. It is therefore recomputed from the sums, which is the
 * scope's actual error against the scope's actual naive benchmark.
 */
export const ForecastDetailAccuracySchema = z.object({
  periodStart: z.string(),
  horizonStep: z.number().int(),
  forecastQty: z.number(),
  actualQty: z.number(),
  absError: z.number(),
  naiveAbsError: z.number().nullable(),
  scaledError: z.number().nullable(),
  /** Distinct sources across the locations scored for this month. */
  actualSources: z.array(z.string()),
  /** Earliest scoring in the scope — how stale the oldest verdict is. */
  scoredAt: z.string(),
  /** Locations contributing to this row. */
  locationCount: z.number(),
});
export type ForecastDetailAccuracy = z.infer<
  typeof ForecastDetailAccuracySchema
>;

// ─── Detail procedure ───────────────────────────────────────────────────────

/**
 * The location filter, shared by the detail read and its month drill-in so the
 * two cannot resolve different scopes for the same URL.
 *
 * Both lists absent means every location that has something to say about this
 * SKU — a forecast series, a policy row, or stock. Both present intersect.
 */
export const ForecastDetailScopeInputSchema = z.object({
  /** `im_products_v3.key`. */
  sku: z.string().min(1),
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  /** Omit for the most recent production run, matching the demand grid. */
  runId: z.string().uuid().optional(),
});

export const GetForecastDetailInputSchema =
  ForecastDetailScopeInputSchema.extend({
    /** Inclusive YYYY-MM-01 bounds on the horizon columns. */
    monthFrom: z.string().optional(),
    monthTo: z.string().optional(),
  });
export type GetForecastDetailInput = z.infer<
  typeof GetForecastDetailInputSchema
>;

export const GetForecastDetailResponseSchema = z.object({
  /**
   * False when the run holds no series for this SKU anywhere in the scope.
   * Everything else still populates — the locations' position and policy are
   * real whether or not the engine forecast them — so the page says "not in
   * this run" over a filled-in header rather than showing nothing.
   */
  inRun: z.boolean(),
  scope: ForecastDetailScopeSchema,
  /** Null when the SKU is not in the catalogue at all. */
  identity: ForecastDetailIdentitySchema.nullable(),
  demand: ForecastDetailDemandSchema,
  run: ForecastDetailRunContextSchema.nullable(),
  policy: ForecastDetailPolicySchema.nullable(),
  position: ForecastDetailPositionSchema,
  sourcePosition: ForecastDetailSourcePositionSchema.nullable(),
  derived: ForecastDetailDerivedSchema,
  /** Live diagnostic from current stock, commitments, policy and customer-sales evidence. */
  stockSurplus: StockSurplusSchema,
  /** Null when the run holds no series in scope. */
  demandCoverage: ForecastDetailDemandCoverageSetSchema.nullable(),
  /** Every scored month for this item and run, independent of display bounds. */
  accuracy: z.array(ForecastDetailAccuracySchema),
});
export type GetForecastDetailResponse = z.infer<
  typeof GetForecastDetailResponseSchema
>;

// ─── Month drill-in ─────────────────────────────────────────────────────────

/**
 * One month of one SKU, split back out by location.
 *
 * This is the write surface behind the aggregate. A demand override is stored
 * per (SKU, location, month), so an aggregated cell cannot be edited as a
 * number — there is no rule for spreading a total back over twelve branches
 * that would not be a guess. The drill-in un-sums the cell instead, and the
 * buyer edits the locations.
 *
 * Fetched only when a cell is opened, which is what keeps the detail read one
 * round trip regardless of how wide the scope is.
 */
export const ForecastMonthLocationSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string().nullable(),
  divisionName: z.string().nullable(),
  /**
   * The number before any override at this location: measured demand for a
   * history month, the engine's curve for a horizon month. Null where the
   * location has nothing for this month.
   */
  baseValue: z.number().nullable(),
  /** What the series actually uses once the override below is applied. */
  effectiveValue: z.number().nullable(),
  /** The active override on this cell, if any. */
  override: z
    .object({
      id: z.string().uuid(),
      mode: z.enum(["zero", "average", "median", "value"]),
      value: z.number().nullable(),
      reason: z.string().nullable(),
      createdAt: z.string(),
      createdByUserName: z.string().nullable(),
    })
    .nullable(),
  /** The growth/shrinkage factor folded into a horizon number, if any. */
  factor: z.number().nullable(),
  /**
   * Whether this row adds into `totals`. `included_in_selected_hub`: a selected
   * hub's recorded footprint already holds this location's demand, named by
   * `coveredByWarehouseId`. `unproven_overlap`: no row adds. Null for a
   * location the run holds no series for. An edit at a covered location still
   * edits that location's own series; it reaches the hub's total only through
   * the hub's next run.
   */
  coverage: z
    .object({
      reason: z.enum([
        "contributing",
        "included_in_selected_hub",
        "legacy_basis",
        "unproven_overlap",
      ]),
      coveredByWarehouseId: z.string().uuid().nullable(),
    })
    .nullable(),
});
export type ForecastMonthLocation = z.infer<typeof ForecastMonthLocationSchema>;

export const GetForecastMonthBreakdownInputSchema =
  ForecastDetailScopeInputSchema.extend({
    /** YYYY-MM-01. */
    month: z.string(),
    kind: z.enum(["history", "forecast"]),
  });
export type GetForecastMonthBreakdownInput = z.infer<
  typeof GetForecastMonthBreakdownInputSchema
>;

export const GetForecastMonthBreakdownResponseSchema = z.object({
  month: z.string(),
  kind: z.enum(["history", "forecast"]),
  locations: z.array(ForecastMonthLocationSchema),
  /**
   * The cell the buyer clicked, so the panel can prove it reconciles: the sum
   * of the rows that add (`coverage` null, contributing or legacy). Null when
   * `totalsUnavailableReason` is set.
   */
  totals: z.object({
    base: z.number().nullable(),
    effective: z.number().nullable(),
  }),
  /**
   * Why `totals` cannot be stated. `unproven_overlap`: the recorded footprints
   * cannot be shown not to overlap. `covering_hub_missing_month`: a selected
   * hub covers a spoke's forecast but wrote no output for this month.
   */
  totalsUnavailableReason: z
    .enum(["unproven_overlap", "covering_hub_missing_month"])
    .nullable(),
});
export type GetForecastMonthBreakdownResponse = z.infer<
  typeof GetForecastMonthBreakdownResponseSchema
>;

// ─── Spoke demand ───────────────────────────────────────────────────────────

/**
 * The spokes the scope serves and what each one sells, at a glance.
 *
 * Buying into a DC is only right if the stores it feeds are moving the item,
 * and as buying shifts to hubs this is the check a planner makes first. A
 * spoke is an active `primary_hub` child of a location in scope that is not
 * itself in scope.
 *
 * Sales are each spoke's own direct customer sales — no transfers, nothing
 * added from locations it serves in turn — for the last `months.length`
 * complete months, from the same customer-sales read the hub demand
 * aggregate uses, so a spoke's row here reconciles with what the hub plans on.
 */
export const ForecastSpokeSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string().nullable(),
  /** Source-system location code (`im_warehouses.origin_id`). */
  warehouseCode: z.string().nullable(),
  divisionName: z.string().nullable(),
  /** The in-scope location this spoke replenishes from. */
  hubWarehouseId: z.string().uuid(),
  hubName: z.string().nullable(),
  /**
   * `reported`: `monthly` holds net customer quantity per month, aligned to
   * `months`. `none_recorded`: no customer sale in the window at all.
   * `unavailable`: the source could not count them (see the response reason).
   */
  salesStatus: z.enum(["reported", "none_recorded", "unavailable"]),
  /** Null unless `salesStatus` is `reported` — never zero-filled. */
  monthly: z.array(z.number()).nullable(),
});
export type ForecastSpoke = z.infer<typeof ForecastSpokeSchema>;

export const GetForecastSpokeDemandInputSchema = ForecastDetailScopeInputSchema;

export const GetForecastSpokeDemandResponseSchema = z.object({
  /** Complete months, ascending; the last is the last complete month. */
  months: z.array(z.string()),
  /**
   * "unavailable" when the source cannot identify customer sales reliably
   * for these locations; `monthly` is then null on every spoke.
   */
  availability: z.enum(["available", "unavailable"]),
  availabilityReason: CustomerSalesUnavailableReasonSchema.nullable(),
  /**
   * `readable_only`: the caller's warehouse privileges are restricted, so the
   * list holds only the spokes (and in-scope hubs) they may read — it is not
   * the hub's full catchment, and how many others exist is not disclosed.
   */
  accessScope: z.enum(["all", "readable_only"]),
  spokes: z.array(ForecastSpokeSchema),
});
export type GetForecastSpokeDemandResponse = z.infer<
  typeof GetForecastSpokeDemandResponseSchema
>;

// ─── Run actuals ────────────────────────────────────────────────────────────

/**
 * What earlier runs forecast for months that have since closed, beside what
 * actually happened — the backward half of the run comparison.
 *
 * A run's horizon starts after its as-of month, so its own months are never
 * closed while it is current; the comparison can only meet actuals by looking
 * at older runs' forecasts for months now in the past. Read from the scored
 * accuracy ledger (`im_forecast_accuracy`), which freezes both numbers when a
 * month closes: re-deriving actuals here would apply today's overrides to a
 * past verdict.
 *
 * One location, like the comparison it serves. A month a run was never scored
 * for is absent, not zero.
 */
export const GetForecastRunActualsInputSchema = z.object({
  /** `im_products_v3.key`. */
  sku: z.string().min(1),
  warehouseId: z.string().uuid(),
  runIds: z.array(z.string().uuid()).min(1).max(4),
});
export type GetForecastRunActualsInput = z.infer<
  typeof GetForecastRunActualsInputSchema
>;

export const ForecastRunActualMonthSchema = z.object({
  /** YYYY-MM-01. */
  periodStart: z.string(),
  /** Months between the run's as-of month and this one. */
  horizonStep: z.number().int(),
  forecastQty: z.number(),
  actualQty: z.number(),
  /** Where the actual came from when it was scored. */
  actualSource: z.string(),
});
export type ForecastRunActualMonth = z.infer<
  typeof ForecastRunActualMonthSchema
>;

export const GetForecastRunActualsResponseSchema = z.object({
  /** One entry per requested run, in request order. */
  runs: z.array(
    z.object({
      runId: z.string().uuid(),
      /** Ascending by month. Empty when the run has no scored month here. */
      months: z.array(ForecastRunActualMonthSchema),
    }),
  ),
});
export type GetForecastRunActualsResponse = z.infer<
  typeof GetForecastRunActualsResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const forecastDetailContracts = {
  get: oc
    .input(GetForecastDetailInputSchema)
    .output(GetForecastDetailResponseSchema)
    .route({
      method: "GET",
      summary: "Get a SKU's forecast detail",
      description:
        "One SKU's forecast summed over chosen divisions or warehouses: demand history, forward forecast, stock position and inventory policy behind it, for a month range.",
    }),
  monthBreakdown: oc
    .input(GetForecastMonthBreakdownInputSchema)
    .output(GetForecastMonthBreakdownResponseSchema)
    .route({
      method: "GET",
      summary: "Break down a SKU's forecast month by location",
      description:
        "Splits one SKU's forecast for one month across each location in scope, showing the active demand override on each.",
    }),
  spokeDemand: oc
    .input(GetForecastSpokeDemandInputSchema)
    .output(GetForecastSpokeDemandResponseSchema)
    .route({
      method: "GET",
      summary: "List the spokes a forecast scope serves",
      description:
        "The spokes a scope serves (active primary-hub children) and each one's direct customer sales by month.",
    }),
  runActuals: oc
    .input(GetForecastRunActualsInputSchema)
    .output(GetForecastRunActualsResponseSchema)
    .route({
      method: "GET",
      summary: "Get earlier forecast runs' scored actuals",
      description:
        "Earlier runs' forecasts for months that have closed, beside the scored actuals, for one SKU at one location.",
    }),
} as const;
